import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const OnboardingContext = createContext(null);

const STORAGE_KEY = 'damyxia_onboarding_v1';
const ONBOARDING_VERSION = '1.0'; // Increment to force re-show after major updates

export const OnboardingProvider = ({ children }) => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [emotionalTone, setEmotionalTone] = useState('neutral'); // neutral | calm | energetic
  const [pendingOnboarding, setPendingOnboarding] = useState(false); // Flag to trigger after MBI

  // Check onboarding status on mount
  useEffect(() => {
    const checkOnboardingStatus = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        
        if (!stored) {
          // First time user - will be triggered after MBI
          setIsFirstTime(true);
          setOnboardingCompleted(false);
          setPendingOnboarding(true);
        } else {
          const data = JSON.parse(stored);
          
          // Check version for updates
          if (data.version !== ONBOARDING_VERSION) {
            setIsFirstTime(false);
            setOnboardingCompleted(false);
            setPendingOnboarding(true);
          } else {
            setIsFirstTime(false);
            setOnboardingCompleted(true);
            setPendingOnboarding(false);
          }
        }
      } catch (error) {
        console.error('Error reading onboarding state:', error);
        setIsFirstTime(true);
        setPendingOnboarding(true);
      }
    };

    checkOnboardingStatus();
  }, []);

  // Start onboarding (called after MBI completes or manually from settings)
  const startOnboarding = useCallback(() => {
    setCurrentStep(0);
    setShowOnboarding(true);
    setPendingOnboarding(false);
  }, []);

  // Trigger onboarding after MBI (called from WelcomeFlow when it completes)
  const triggerAfterMBI = useCallback(() => {
    if (pendingOnboarding && !onboardingCompleted) {
      // Small delay to let MBI fully close
      setTimeout(() => {
        startOnboarding();
      }, 500);
    }
  }, [pendingOnboarding, onboardingCompleted, startOnboarding]);

  // Complete onboarding
  const completeOnboarding = useCallback(() => {
    try {
      const data = {
        version: ONBOARDING_VERSION,
        completedAt: new Date().toISOString(),
        platform: detectPlatform()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      setShowOnboarding(false);
      setOnboardingCompleted(true);
      setCurrentStep(0);
      setPendingOnboarding(false);
    } catch (error) {
      console.error('Error saving onboarding state:', error);
      setShowOnboarding(false);
    }
  }, []);

  // Reset onboarding (for settings option)
  const resetOnboarding = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setOnboardingCompleted(false);
      setCurrentStep(0);
      startOnboarding();
    } catch (error) {
      console.error('Error resetting onboarding:', error);
    }
  }, [startOnboarding]);

  // Skip/close onboarding
  const skipOnboarding = useCallback(() => {
    setShowOnboarding(false);
    setCurrentStep(0);
    // Don't mark as complete when skipping - they can restart from settings
  }, []);

  // Navigate steps
  const nextStep = useCallback((totalSteps) => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeOnboarding();
    }
  }, [currentStep, completeOnboarding]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback((step) => {
    setCurrentStep(step);
  }, []);

  // Set emotional tone from AEAE
  const setTone = useCallback((tone) => {
    if (['neutral', 'calm', 'energetic'].includes(tone)) {
      setEmotionalTone(tone);
    }
  }, []);

  // 🟥 CUADRO NEGRO FIX GENIO (feb-2026) — Exponer una API global para
  // abrir el tour desde cualquier punto de la app (ej. botón "Onboarding"
  // del panel del Lyon en el Genio, que vive fuera de UiBuilderPage).
  // Antes solo se registraba dentro de UiBuilderPage.jsx → si estabas
  // en la home o dashboard, el botón quedaba "muerto" (sin error pero
  // sin abrir nada). Acá vive en el provider que SIEMPRE está montado.
  // También escuchamos un evento custom para integraciones que prefieran
  // CustomEvent en vez de tocar window.*.
  useEffect(() => {
    const open = () => { startOnboarding(); };
    window.__damyxia_openOnboarding = open;
    const onCustomEvent = () => open();
    window.addEventListener('damyxia:open-onboarding', onCustomEvent);
    return () => {
      if (window.__damyxia_openOnboarding === open) {
        try { delete window.__damyxia_openOnboarding; } catch (_) { window.__damyxia_openOnboarding = undefined; }
      }
      window.removeEventListener('damyxia:open-onboarding', onCustomEvent);
    };
  }, [startOnboarding]);

  const value = {
    showOnboarding,
    onboardingCompleted,
    isFirstTime,
    currentStep,
    emotionalTone,
    pendingOnboarding,
    startOnboarding,
    triggerAfterMBI,
    completeOnboarding,
    resetOnboarding,
    skipOnboarding,
    nextStep,
    prevStep,
    goToStep,
    setTone,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

// Helper function to detect platform
const detectPlatform = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
    return 'ios';
  }
  if (/android/i.test(ua)) {
    return 'android';
  }
  if (window.innerWidth >= 1024) {
    return 'desktop';
  }
  return 'mobile';
};

// Custom hook to use Onboarding context
export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export default OnboardingContext;
